import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Mail, Phone, MapPin, Building2, Calendar, FileText, User } from 'lucide-react';
import { format, parseISO } from 'date-fns';

// Helper function to format date without timezone issues
const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    // If it's just a date string like "1997-09-15", parse it directly
    if (dateString.length === 10 && dateString.includes('-')) {
      const [year, month, day] = dateString.split('-');
      return format(new Date(parseInt(year), parseInt(month) - 1, parseInt(day)), 'MMM dd, yyyy');
    }
    // Otherwise try parseISO for full ISO strings
    return format(parseISO(dateString), 'MMM dd, yyyy');
  } catch {
    return dateString;
  }
};

const ClientOverviewTab = ({ 
  client, 
  customFields, 
  linkedCorporations, 
  ownerClient 
}) => {
  const navigate = useNavigate();
  
  // Filter custom fields applicable to this client type
  const applicableCustomFields = customFields.filter(field => 
    field.appliesTo === 'all' || field.appliesTo === client.type?.toLowerCase()
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Contact Information */}
        <Card>
          <CardHeader>
            <CardTitle>Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3">
              <Mail className="h-4 w-4 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="text-sm font-medium text-gray-900">{client.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="h-4 w-4 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="text-sm font-medium text-gray-900">{client.phone || '-'}</p>
              </div>
            </div>
            {client.address && typeof client.address === 'object' && client.address.line1 && (
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Address</p>
                  <p className="text-sm font-medium text-gray-900">
                    {client.address.line1}
                    {client.address.line2 && <>, {client.address.line2}</>}
                    <br />
                    {client.address.city && <>{client.address.city}, </>}
                    {client.address.province && <>{client.address.province} </>}
                    {client.address.postalCode}
                    {client.address.country && <><br />{client.address.country}</>}
                  </p>
                </div>
              </div>
            )}
            <div className="flex items-center gap-3">
              <Building2 className="h-4 w-4 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Type</p>
                <p className="text-sm font-medium text-gray-900">{client.type}</p>
              </div>
            </div>
            
            {/* Individual: Show DOB, Marital Status, SIN, GST/HST, Payroll */}
            {client.type === 'Individual' && (
              <>
                {client.dateOfBirth && (
                  <div className="flex items-center gap-3">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Date of Birth</p>
                      <p className="text-sm font-medium text-gray-900">
                        {formatDate(client.dateOfBirth)}
                      </p>
                    </div>
                  </div>
                )}
                {client.maritalStatus && (
                  <div className="flex items-center gap-3">
                    <User className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Marital Status</p>
                      <p className="text-sm font-medium text-gray-900">{client.maritalStatus}</p>
                    </div>
                  </div>
                )}
                {client.sin && (
                  <div className="flex items-center gap-3">
                    <FileText className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">SIN</p>
                      <p className="text-sm font-medium text-gray-900">{client.sin}</p>
                    </div>
                  </div>
                )}
                {client.gstHstNumber && (
                  <div className="flex items-center gap-3">
                    <FileText className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">GST/HST Number</p>
                      <p className="text-sm font-medium text-gray-900">{client.gstHstNumber}</p>
                    </div>
                  </div>
                )}
                {client.payrollNumber && (
                  <div className="flex items-center gap-3">
                    <FileText className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Payroll Number</p>
                      <p className="text-sm font-medium text-gray-900">{client.payrollNumber}</p>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Corporation: Show BN, GST/HST, Payroll */}
            {client.type === 'Corporation' && (
              <>
                {client.businessNumber && (
                  <div className="flex items-center gap-3">
                    <FileText className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Business Number (BN)</p>
                      <p className="text-sm font-medium text-gray-900">{client.businessNumber}</p>
                    </div>
                  </div>
                )}
                {client.gstHstNumber && (
                  <div className="flex items-center gap-3">
                    <FileText className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">GST/HST Number</p>
                      <p className="text-sm font-medium text-gray-900">{client.gstHstNumber}</p>
                    </div>
                  </div>
                )}
                {client.payrollNumber && (
                  <div className="flex items-center gap-3">
                    <FileText className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Payroll Number</p>
                      <p className="text-sm font-medium text-gray-900">{client.payrollNumber}</p>
                    </div>
                  </div>
                )}
                {client.yearEnd && (
                  <div className="flex items-center gap-3">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Year End</p>
                      <p className="text-sm font-medium text-gray-900">{client.yearEnd}</p>
                    </div>
                  </div>
                )}
              </>
            )}
            
            <div className="flex items-center gap-3">
              <Calendar className="h-4 w-4 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Last Activity</p>
                <p className="text-sm font-medium text-gray-900">
                  {client.lastActivity ? format(new Date(client.lastActivity), 'MMM dd, yyyy h:mm a') : 'N/A'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Calendar className="h-4 w-4 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Date Added</p>
                <p className="text-sm font-medium text-gray-900">
                  {client.createdAt ? format(new Date(client.createdAt), 'MMM dd, yyyy') : 'N/A'}
                  {client.createdByName && (
                    <span className="text-gray-500 text-xs ml-1">by {client.createdByName}</span>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Custom Fields */}
        <Card>
          <CardHeader>
            <CardTitle>Custom Fields</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {applicableCustomFields.map((field) => (
              <div key={field._id} className="flex flex-col gap-1">
                <span className="text-sm text-gray-500">{field.name}</span>
                {field.type === 'conditional_number' ? (
                  <div className="text-sm font-medium text-gray-900">
                    {(() => {
                      const val = client.customFields?.[field.fieldId];
                      if (!val || !val.count) return '-';
                      return (
                        <div className="space-y-2">
                          <span className="text-blue-600">{val.count} {field.name.toLowerCase()}</span>
                          {val.items?.slice(0, val.count).map((item, idx) => (
                            <div key={idx} className="ml-4 p-2 bg-gray-50 rounded text-xs">
                              <span className="font-semibold">#{idx + 1}:</span>
                              {field.conditionalConfig?.subFields?.map(sf => (
                                <span key={sf.id} className="ml-2">
                                  {sf.name}: {item?.[sf.id] || '-'}
                                </span>
                              ))}
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                ) : field.type === 'conditional_dropdown' ? (
                  <div className="text-sm font-medium text-gray-900">
                    {(() => {
                      const val = client.customFields?.[field.fieldId];
                      if (!val || !val.selectedOption) return '-';
                      const selectedLabel = val.selectedLabel || val.selectedOption;
                      const subFields = field.conditionalConfig?.conditions?.[val.selectedOption] || [];
                      const fieldValues = val.fieldValues?.[val.selectedOption] || {};
                      return (
                        <div className="space-y-2">
                          <span className="text-purple-600">{selectedLabel}</span>
                          {subFields.length > 0 && (
                            <div className="ml-4 p-2 bg-purple-50 rounded text-xs">
                              {subFields.map(sf => (
                                <div key={sf.id}>
                                  <span className="font-semibold">{sf.name}:</span> {fieldValues[sf.id] || '-'}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <span className="text-sm font-medium text-gray-900">
                    {client.customFields?.[field.fieldId] || '-'}
                  </span>
                )}
              </div>
            ))}
            {applicableCustomFields.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">No custom fields configured</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Spouse Information Card - Only for Individuals with spouse */}
      {client.type === 'Individual' && client.spouse?.name && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Spouse Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="text-sm font-medium text-gray-900">{client.spouse.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Date of Birth</p>
                <p className="text-sm font-medium text-gray-900">
                  {formatDate(client.spouse.dateOfBirth)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="text-sm font-medium text-gray-900">{client.spouse.email || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="text-sm font-medium text-gray-900">{client.spouse.phone || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">SIN</p>
                <p className="text-sm font-medium text-gray-900">{client.spouse.sin || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Children Information Card - Only for Individuals with children */}
      {client.type === 'Individual' && client.children && client.children.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Children ({client.children.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {client.children.map((child, index) => (
                <div key={index} className="p-3 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Name</p>
                      <p className="text-sm font-medium text-gray-900">{child.name || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Date of Birth</p>
                      <p className="text-sm font-medium text-gray-900">
                        {formatDate(child.dateOfBirth)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">SIN</p>
                      <p className="text-sm font-medium text-gray-900">{child.sin || '-'}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Linked Corporations Card - Only for Individuals */}
      {client.type === 'Individual' && linkedCorporations.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Linked Corporations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {linkedCorporations.map((corp) => (
                <div 
                  key={corp._id} 
                  className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer flex items-center justify-between"
                  onClick={() => navigate(`/clients/${corp._id}`)}
                >
                  <div>
                    <p className="font-medium text-gray-900">{corp.name}</p>
                    <p className="text-sm text-gray-500">{corp.clientCode && `(${corp.clientCode})`}</p>
                  </div>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700">Corporation</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Owner Information Card - Only for Corporations */}
      {client.type === 'Corporation' && ownerClient && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Connected Individual (Owner)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div 
              className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer flex items-center justify-between"
              onClick={() => navigate(`/clients/${ownerClient._id}`)}
            >
              <div className="flex-1">
                <p className="font-medium text-gray-900">{ownerClient.name}</p>
                <p className="text-sm text-gray-500">{ownerClient.clientCode && `(${ownerClient.clientCode})`}</p>
                <div className="flex gap-4 mt-1 text-xs text-gray-500">
                  {ownerClient.email && <span>{ownerClient.email}</span>}
                  {ownerClient.phone && <span>{ownerClient.phone}</span>}
                </div>
              </div>
              <Badge variant="outline" className="bg-green-50 text-green-700">Individual</Badge>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ClientOverviewTab;
